/**
 * Phase D2 — Bloc débriefing RDV (mode "à débriefer" ou "clôturé + éditable").
 *
 * Mobile-first : cas d'usage réel = "45s de dictée depuis la voiture". Priorité
 * gros bouton micro (≥56px), inline sans modal, feedback visuel enregistrement.
 *
 * Props :
 *   rdv          : objet RDV (avec computed_needs_debrief, realization_status,
 *                  debrief_note, debrief_closed_at, debrief_edit_history…)
 *   leadId       : id du lead rattaché (pour redirect follow-up quote/task/etc.)
 *   chantierId?  : id du chantier (utile pour follow-up "quote")
 *   onUpdated    : callback appelé après tout mutation (status/debrief/cancel)
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle2, UserX, CalendarClock, XCircle, Mic, MicOff,
  Loader2, ClipboardCheck, Pencil, ArrowRight, History,
} from "lucide-react";

const REALIZATION_META = {
  effectue:  { label: "Effectué",       Icon: CheckCircle2,  cls: "badge-success" },
  absent:    { label: "Client absent",  Icon: UserX,         cls: "badge-warning" },
  reporte:   { label: "Reporté",        Icon: CalendarClock, cls: "badge-warning" },
  annule:    { label: "Annulé",         Icon: XCircle,       cls: "badge-error" },
};

const FOLLOW_UP_OPTIONS = [
  { value: "quote",           label: "Créer un devis" },
  { value: "new_rdv",         label: "Planifier un nouveau RDV" },
  { value: "task",            label: "Créer une tâche" },
  { value: "project_outcome", label: "Marquer l'issue du projet" },
];

function fmt(dt) {
  try { return new Date(dt).toLocaleString("fr-BE", { dateStyle: "short", timeStyle: "short" }); }
  catch { return dt; }
}

export default function RdvDebriefBlock({ rdv, leadId, chantierId, onUpdated }) {
  const navigate = useNavigate();
  const closed = !!rdv.debrief_closed_at;
  const cancelled = !!rdv.cancelled_at;
  const [editingNote, setEditingNote] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // ---------------------------------------------------------------------
  // Modes d'affichage :
  // - cancelled       : RDV annulé (badge + raison, pas de débrief)
  // - closed (!editing) : lecture seule du débrief + crayon "modifier note"
  // - closed (editing)  : édition inline note post-clôture
  // - "à débriefer"    : bloc de saisie complet (statut + note + dictée + follow-up)
  // ---------------------------------------------------------------------
  if (cancelled) {
    return (
      <div className="rounded-md border border-error/40 bg-error-subtle/20 p-3" data-testid="rdv-cancelled-view">
        <div className="flex items-center gap-2">
          <XCircle className="h-4 w-4 text-error-text" />
          <span className="text-sm font-medium text-error-text">RDV annulé</span>
          <span className="ml-auto text-xs text-platform-muted">{fmt(rdv.cancelled_at)}</span>
        </div>
        {rdv.cancellation_reason && (
          <p className="mt-1 text-xs text-platform-body">Raison : {rdv.cancellation_reason}</p>
        )}
      </div>
    );
  }

  if (closed && !editingNote) {
    return <ClosedView rdv={rdv} onEdit={() => setEditingNote(true)} onToggleHistory={() => setShowHistory(h => !h)} showHistory={showHistory} />;
  }
  if (closed && editingNote) {
    return <NoteEditor rdv={rdv} onCancel={() => setEditingNote(false)} onSaved={() => { setEditingNote(false); onUpdated?.(); }} />;
  }
  if (rdv.computed_needs_debrief) {
    return <DebriefEditor rdv={rdv} leadId={leadId} chantierId={chantierId} navigate={navigate} onUpdated={onUpdated} />;
  }
  return null;
}

// =====================================================================
// Mode 1 — Éditeur débrief (RDV à débriefer)
// =====================================================================
function DebriefEditor({ rdv, leadId, chantierId, navigate, onUpdated }) {
  const [status, setStatus] = useState(rdv.realization_status || null);
  const [note, setNote] = useState(rdv.debrief_note || "");
  const [cancelReason, setCancelReason] = useState("");
  const [followUp, setFollowUp] = useState(null);
  // Phase RDV↔Devis F2 (2026-09-09) — ID d'un devis existant à lier (option
  // "Lier un devis existant" du picker). Si set, court-circuite la création
  // brouillon dans closeDebrief. Reset quand followUp change.
  const [followUpRefId, setFollowUpRefId] = useState(null);
  const [existingQuotes, setExistingQuotes] = useState([]);
  const [linkMode, setLinkMode] = useState("create"); // "create" | "link"
  const [submitting, setSubmitting] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  // Fetch devis liables (mêmes lead, non-supprimés, non déjà liés à un autre RDV)
  // quand l'user sélectionne "Lier un devis existant".
  useEffect(() => {
    if (followUp !== "quote" || linkMode !== "link" || !leadId) return;
    let cancelled = false;
    api
      .get(`/quotes?lead_id=${leadId}&page_size=100`)
      .then((res) => {
        if (cancelled) return;
        const items = res.data?.items || [];
        const available = items.filter((q) => !q.rdv_id || q.rdv_id === rdv.id);
        available.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
        setExistingQuotes(available);
      })
      .catch(() => { if (!cancelled) setExistingQuotes([]); });
    return () => { cancelled = true; };
  }, [followUp, linkMode, leadId, rdv.id]);

  const canClose = status && (
    status === "annule"
      ? cancelReason.trim().length > 0
      : (followUp && (followUp !== "quote" || linkMode !== "link" || followUpRefId))
  );

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      recorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "debrief.webm");
          const r = await api.post("/poc/transcribe", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const text = (r.data?.transcription || "").trim();
          // Append (pas overwrite) — permet plusieurs dictées enchaînées
          setNote((prev) => (prev ? prev + " " : "") + text);
        } catch (err) {
          toast.error(formatApiError(err, "Transcription impossible"));
        } finally {
          setTranscribing(false);
        }
      };
      mr.start();
      setRecording(true);
    } catch {
      toast.error("Impossible d'accéder au micro. Vérifie les permissions du navigateur.");
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    setRecording(false);
  };

  const closeDebrief = async () => {
    setSubmitting(true);
    try {
      if (status === "annule") {
        await api.post(`/rendezvous/${rdv.id}/cancel`, { cancellation_reason: cancelReason.trim() });
        toast.success("RDV marqué annulé");
        onUpdated?.();
      } else {
        // 1) Fixer le realization_status si différent de "effectue" (défault du backend)
        if (status && status !== "effectue") {
          await api.patch(`/rendezvous/${rdv.id}/status`, { realization_status: status });
        }
        // Phase RDV↔Devis fix 2026-09-09 (F1) — Ordre spécial pour "Créer un devis" :
        // on crée d'abord un brouillon vide via POST /quotes (lead_id + chantier_id),
        // puis on soumet le débrief AVEC `follow_up_ref_id=<new_quote_id>` pour que
        // le hook backend (rendezvous_routes.py:577-604) crée directement le lien
        // symétrique Quote.rdv_id + Rendezvous.follow_up_ref_id. Enfin on navigue
        // vers /quotes/{id} (route existante) au lieu de /quotes/new (route cassée
        // qui déclenchait GET /api/quotes/new → 404 "Devis introuvable" + spinner).
        let createdQuoteId = null;
        if (followUp === "quote") {
          const linkedQuoteId = followUpRefId;  // sélection F2 "Lier un devis existant"
          if (linkedQuoteId) {
            createdQuoteId = linkedQuoteId;
          } else {
            const draftPayload = { lead_id: leadId, lines: [] };
            if (chantierId) draftPayload.chantier_id = chantierId;
            const draftRes = await api.post("/quotes", draftPayload);
            createdQuoteId = draftRes.data?.id;
          }
        }
        // 2) Clôturer le débrief (avec follow_up_ref_id si quote → hook crée le lien)
        const debriefPayload = {
          note: note.trim() || "(note vide)",
          follow_up_action: followUp,
        };
        if (followUp === "quote" && createdQuoteId) {
          debriefPayload.follow_up_ref_id = createdQuoteId;
        }
        await api.post(`/rendezvous/${rdv.id}/debrief`, debriefPayload);
        toast.success("Débrief clôturé");
        onUpdated?.();
        // 3) Redirect selon follow-up
        setTimeout(() => {
          if (followUp === "quote" && createdQuoteId) navigate(`/quotes/${createdQuoteId}`);
          else if (followUp === "new_rdv") navigate(`/projects/${leadId}?open_rdv=1`);
          else if (followUp === "task") navigate(`/projects/${leadId}?open_task=1`);
          else if (followUp === "project_outcome") {
            toast.info("Marquage d'issue du projet — à définir en D3");
            navigate(`/projects/${leadId}`);
          }
        }, 300);
      }
    } catch (err) {
      toast.error(formatApiError(err, "Erreur lors de la clôture"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-lg border border-warning/60 bg-warning-subtle/20 p-4 space-y-4" data-testid="rdv-debrief-editor">
      <div className="flex items-center gap-2">
        <ClipboardCheck className="h-4 w-4 text-warning-text" />
        <div className="text-sm font-semibold text-platform-heading">Ce RDV attend un débrief</div>
      </div>

      {/* 4 boutons statut réalisation — mobile: wrap 2×2 */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {Object.entries(REALIZATION_META).map(([key, m]) => {
          const active = status === key;
          const Icon = m.Icon;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setStatus(key)}
              className={`flex min-h-[44px] items-center justify-center gap-1.5 rounded-md border px-2 py-2 text-sm transition-colors ${
                active ? m.cls + " border" : "border-platform-border bg-platform-surface text-platform-body hover:bg-platform-hover"
              }`}
              data-testid={`rdv-status-${key}`}
            >
              <Icon className="h-4 w-4" />
              <span>{m.label}</span>
            </button>
          );
        })}
      </div>

      {status === "annule" ? (
        <div className="space-y-2">
          <label className="text-xs font-medium text-platform-body">Raison de l'annulation *</label>
          <Input
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Ex. Client injoignable, reporté sine die…"
            className="border-platform-border bg-platform-surface"
            data-testid="rdv-cancel-reason"
          />
        </div>
      ) : status && (
        <>
          {/* Zone note + dictée */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-platform-body">Note de débrief</label>
              {transcribing && <span className="text-xs text-warning-text inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Transcription…</span>}
            </div>
            <Textarea
              rows={4}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ce que le client veut, contraintes chantier, prochaines étapes…"
              className="border-platform-border bg-platform-surface"
              data-testid="rdv-debrief-note"
            />
            {/* Bouton micro géant mobile-first ≥56px */}
            <button
              type="button"
              onClick={recording ? stopRecording : startRecording}
              disabled={transcribing}
              className={`flex min-h-[56px] w-full items-center justify-center gap-2 rounded-lg border text-sm font-medium transition-colors ${
                recording
                  ? "border-error/60 bg-error-subtle/40 text-error-text animate-pulse"
                  : "border-[#C4553B]/60 bg-[#C4553B]/10 text-[#C4553B] hover:bg-[#C4553B]/20"
              }`}
              data-testid="rdv-debrief-mic"
            >
              {recording ? <><MicOff className="h-5 w-5" /> Arrêter et transcrire</> : <><Mic className="h-5 w-5" /> Dicter le débrief</>}
            </button>
          </div>

          {/* Sélecteur "Suite à donner" — obligatoire.
              Hiérarchie B.1 (2026-09-09) : quand followUp="quote", les 2 sous-options
              (Créer / Lier) REMPLACENT le grid des 4 options (bouton "← Retour"
              pour revenir aux 4 options). Évite l'empilement niveau 1 + niveau 2. */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-platform-body">Suite à donner *</label>
            {followUp !== "quote" && (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {FOLLOW_UP_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setFollowUp(opt.value)}
                    className={`flex min-h-[44px] items-center justify-between rounded-md border px-3 py-2 text-sm transition-colors ${
                      followUp === opt.value
                        ? "border-[#C4553B]/60 bg-[#C4553B]/15 text-platform-heading"
                        : "border-platform-border bg-platform-surface text-platform-body hover:bg-platform-hover"
                    }`}
                    data-testid={`rdv-followup-${opt.value}`}
                  >
                    <span>{opt.label}</span>
                    {followUp === opt.value && <ArrowRight className="h-4 w-4 text-[#C4553B]" />}
                  </button>
                ))}
              </div>
            )}
            {/* Phase RDV↔Devis F2 — Mode "Créer" vs "Lier un devis existant". */}
            {followUp === "quote" && (
              <div className="mt-3 space-y-2 rounded-md border border-platform-border bg-platform-surface/50 p-3" data-testid="debrief-followup-mode-select">
                <button
                  type="button"
                  onClick={() => { setFollowUp(null); setLinkMode("create"); setFollowUpRefId(null); }}
                  className="flex items-center gap-1 text-xs text-platform-body hover:text-platform-heading"
                  data-testid="debrief-back-to-followup-options"
                >
                  <ArrowRight className="h-3 w-3 rotate-180" />
                  Retour
                </button>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setLinkMode("create"); setFollowUpRefId(null); }}
                    className={`flex-1 rounded-md border px-3 py-2 text-xs ${linkMode === "create" ? "border-[#C4553B]/60 bg-[#C4553B]/10 text-[#C4553B]" : "border-platform-border bg-platform-surface text-platform-body"}`}
                    data-testid="debrief-mode-create"
                  >
                    Créer un nouveau devis
                  </button>
                  <button
                    type="button"
                    onClick={() => setLinkMode("link")}
                    className={`flex-1 rounded-md border px-3 py-2 text-xs ${linkMode === "link" ? "border-[#C4553B]/60 bg-[#C4553B]/10 text-[#C4553B]" : "border-platform-border bg-platform-surface text-platform-body"}`}
                    data-testid="debrief-mode-link"
                  >
                    Lier un devis existant
                  </button>
                </div>
                {linkMode === "link" && (
                  <div className="space-y-1" data-testid="debrief-link-existing-quote-picker">
                    {existingQuotes.length === 0 ? (
                      <div className="text-xs italic text-platform-body">Aucun devis liable pour ce projet.</div>
                    ) : (
                      <select
                        value={followUpRefId || ""}
                        onChange={(e) => setFollowUpRefId(e.target.value || null)}
                        className="w-full rounded-md border border-platform-border bg-platform-surface px-2 py-2 text-sm"
                        data-testid="debrief-link-existing-quote-select"
                      >
                        <option value="">Sélectionner un devis…</option>
                        {existingQuotes.map((q) => (
                          <option
                            key={q.id}
                            value={q.id}
                            data-testid={`debrief-quote-option-${q.id}`}
                          >
                            {q.number || q.id.slice(0, 8)} — {(q.total_tvac || 0).toFixed(2)} € — {q.status} — {(q.created_at || "").slice(0, 10)}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Bouton clôturer */}
      <Button
        type="button"
        onClick={closeDebrief}
        disabled={!canClose || submitting}
        className="w-full min-h-[48px] bg-[#C4553B] text-white hover:bg-[#A94531] disabled:opacity-40"
        data-testid="rdv-debrief-close-btn"
      >
        {submitting
          ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Enregistrement…</>
          : status === "annule" ? "Confirmer l'annulation" : "Clôturer le débrief"}
      </Button>
    </div>
  );
}

// =====================================================================
// Mode 2 — Vue clôturée (lecture + éditer + historique)
// =====================================================================
function LinkedQuoteLine({ quoteId }) {
  // Phase RDV↔Devis (2026-09-09) — fetch minimal du numéro devis pour affichage.
  const [number, setNumber] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api
      .get(`/quotes/${quoteId}`)
      .then((res) => { if (!cancelled) setNumber(res.data?.number || quoteId.slice(0, 8)); })
      .catch(() => { if (!cancelled) setNumber(quoteId.slice(0, 8)); });
    return () => { cancelled = true; };
  }, [quoteId]);
  return (
    <div className="text-sm">
      Devis créé :{" "}
      <Link
        to={`/quotes/${quoteId}`}
        className="text-[#C4553B] underline underline-offset-2 hover:text-[#A03D2C]"
        data-testid="rdv-linked-quote-link"
      >
        {number || "…"}
      </Link>
    </div>
  );
}

function ClosedView({ rdv, onEdit, onToggleHistory, showHistory }) {
  const meta = REALIZATION_META[rdv.realization_status] || REALIZATION_META.effectue;
  const followUp = FOLLOW_UP_OPTIONS.find(o => o.value === rdv.follow_up_action);
  const hasHistory = (rdv.debrief_edit_history?.length || 0) > 0;
  return (
    <div className="rounded-lg border border-platform-border bg-platform-surface p-4 space-y-3" data-testid="rdv-debrief-closed">
      <div className="flex items-center gap-2">
        <Badge className={`border ${meta.cls}`}>{meta.label}</Badge>
        {followUp && <Badge className="border badge-info">Suite : {followUp.label}</Badge>}
        <span className="ml-auto text-xs text-platform-muted">Clôturé le {fmt(rdv.debrief_closed_at)}</span>
        <button
          onClick={onEdit}
          className="rounded p-1 text-platform-muted hover:bg-platform-hover hover:text-[#C4553B]"
          title="Modifier la note"
          data-testid="rdv-debrief-edit-btn"
        >
          <Pencil className="h-4 w-4" />
        </button>
      </div>
      <p className="whitespace-pre-wrap text-sm text-platform-body" data-testid="rdv-debrief-note-text">
        {rdv.debrief_note || "(note vide)"}
      </p>
      {rdv.follow_up_action === "quote" && rdv.follow_up_ref_id && (
        <LinkedQuoteLine quoteId={rdv.follow_up_ref_id} />
      )}
      {hasHistory && (
        <>
          <button
            onClick={onToggleHistory}
            className="inline-flex items-center gap-1 text-xs text-platform-muted hover:text-[#C4553B]"
            data-testid="rdv-debrief-history-toggle"
          >
            <History className="h-3 w-3" />
            Modifié {rdv.debrief_edit_history.length} fois {showHistory ? "▲" : "▼"}
          </button>
          {showHistory && (
            <ul className="space-y-1 border-l-2 border-platform-border pl-3 text-xs text-platform-muted" data-testid="rdv-debrief-history">
              {rdv.debrief_edit_history.map((h, i) => (
                <li key={i}>
                  <span className="text-platform-body">{fmt(h.edited_at)}</span> — {h.previous_note?.slice(0, 100) || "(vide)"}{h.previous_note?.length > 100 ? "…" : ""}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

// =====================================================================
// Mode 3 — Éditeur note post-clôture (PATCH /debrief)
// =====================================================================
function NoteEditor({ rdv, onCancel, onSaved }) {
  const [note, setNote] = useState(rdv.debrief_note || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      await api.patch(`/rendezvous/${rdv.id}/debrief`, { note: note.trim() });
      toast.success("Note mise à jour");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="rounded-lg border border-warning/60 bg-warning-subtle/20 p-4 space-y-3" data-testid="rdv-debrief-note-editor">
      <div className="flex items-center gap-2">
        <Pencil className="h-4 w-4 text-warning-text" />
        <span className="text-sm font-medium text-platform-heading">Modifier la note de débrief</span>
      </div>
      <Textarea
        rows={4}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        className="border-platform-border bg-platform-surface"
        data-testid="rdv-debrief-note-edit-textarea"
      />
      <div className="flex gap-2">
        <Button onClick={save} disabled={saving || !note.trim()} className="flex-1 bg-[#C4553B] text-white hover:bg-[#A94531]" data-testid="rdv-debrief-note-save-btn">
          {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
          Enregistrer
        </Button>
        <Button onClick={onCancel} variant="outline" className="border-platform-border">Annuler</Button>
      </div>
    </div>
  );
}
